import { Logo } from './Logo';
import { Ic, HeartFilled, Badge } from './ui';
import { formatBRL, formatDate } from '../data/constants';
import { useTheme } from '../context/ThemeContext';
import { openPropertyWindow } from '../services/propertyWindow';
import { getEnrichment } from '../services/batchAnalysis';
import { getPaymentMethods, getModalidadeStyle, PAYMENT_SHORT, PAYMENT_LABEL } from '../data/paymentMethods';

const BANK_COLORS = {
  Caixa:     { bg: '#005CA9', text: '#fff' },
  Santander: { bg: '#EC0000', text: '#fff' },
};

const TYPE_GRADIENT = {
  Apartamento: 'linear-gradient(135deg, #1a2744 0%, #0f172a 100%)',
  Casa:        'linear-gradient(135deg, #1a2a1a 0%, #0f1f0f 100%)',
  Terreno:     'linear-gradient(135deg, #2a1f0f 0%, #1a1208 100%)',
  Comercial:   'linear-gradient(135deg, #1a1a2a 0%, #0f0f1f 100%)',
};

export function PropertyCard({ property: p, favorites, toggleFav, viewMode, enriched }) {
  const { dark } = useTheme();
  const isFav = favorites.has(p.id);
  // Time-to-end: Venda Online uses hour granularity (Caixa listings end at
  // specific hours). Everything else uses day granularity.
  const msLeft      = new Date(p.dataLeilao) - new Date();
  const hoursLeft   = msLeft / 3_600_000;
  const daysLeft    = Math.ceil(msLeft / 86_400_000);
  const isVendaOnline = p.modalidade === 'Venda Online';
  // Ribbon label: hours when <24h remaining, else days (matches Santander "5 DIAS" style)
  let ribbonLabel   = null;
  if (daysLeft >= 0 && daysLeft <= 7) {
    if (hoursLeft < 1) ribbonLabel = 'HOJE';
    else if (hoursLeft < 24) ribbonLabel = `${Math.ceil(hoursLeft)}H`;
    else if (daysLeft === 0) ribbonLabel = 'HOJE';
    else ribbonLabel = `${daysLeft} DIAS`;
  }
  // Caixa Venda Online = blue ribbon (countdown to sale end), otherwise red
  // (matches existing Santander auction urgency styling).
  const ribbonBg = isVendaOnline
    ? (hoursLeft < 24 ? '#1d4ed8' : 'rgba(37,99,235,.85)')
    : (daysLeft === 0 ? 'var(--rd)' : 'rgba(220,38,38,.85)');
  const isInactive = p.status === 'inativo';
  const statusVariant = isInactive ? 'red' : (p.status === 'Aberto' || p.status === 'ativo') ? 'green' : p.status === 'Disputa' ? 'orange' : 'red';
  const statusLabel = isInactive ? 'Indisponível' : p.status;
  const inactiveStyle = isInactive ? { opacity: 0.6, filter: 'grayscale(.3)' } : {};
  const bankStyle = BANK_COLORS[p.banco] || { bg: 'var(--s3)', text: 'var(--tx)' };

  const enrichment = enriched ? getEnrichment(p.id) : null;
  const handleClick = () => openPropertyWindow(p, dark, enrichment);
  const modStyle = getModalidadeStyle(p.modalidade);
  const modBg = dark ? modStyle.darkBg : modStyle.bg;
  const modFg = dark ? modStyle.darkFg : modStyle.fg;
  const payments = p.banco === 'Caixa' ? getPaymentMethods(p) : [];

  if (viewMode === 'list') {
    return (
      <div
        className="property-card-list"
        onClick={handleClick}
        style={{
          display: 'flex', gap: 12, padding: '12px 14px',
          background: 'var(--sf)', border: '1px solid var(--bd)',
          borderRadius: 10, cursor: 'pointer', alignItems: 'center',
          transition: 'border-color .2s, box-shadow .2s',
          ...inactiveStyle,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brl)'; e.currentTarget.style.boxShadow = 'var(--shadow-sm)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        {/* Thumbnail */}
        <div style={{
          width: 48, height: 48, borderRadius: 8, flexShrink: 0,
          background: TYPE_GRADIENT[p.tipo] || 'var(--ct)',
          backgroundImage: p.images?.[0] ? `url(${p.images[0]})` : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {!p.images?.[0] && <Logo size={20} />}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-.1px' }}>
            {p.titulo}
          </div>
          <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {p.area ? <span>{p.area}m²</span> : null}
            {p.quartos ? <span>{p.quartos}q</span> : null}
            <span style={{ background: modBg, color: modFg, padding: '1px 7px', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '.3px', textTransform: 'uppercase' }}>
              {p.modalidade}
            </span>
            {payments.map((pm) => (
              <span key={pm} title={PAYMENT_LABEL[pm]} style={{ background: 'var(--s2)', color: 'var(--t2)', padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 600 }}>
                {PAYMENT_SHORT[pm]}
              </span>
            ))}
          </div>
        </div>

        {/* Badges */}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0 }}>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
          {enriched && (
            <span style={{ background: 'var(--al)', color: 'var(--ac)', border: '1px solid rgba(37,99,235,.15)', padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '.5px' }}>IA</span>
          )}
        </div>

        {/* Price */}
        <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 90 }}>
          <div style={{ fontSize: 10, color: 'var(--t3)', textDecoration: 'line-through' }}>{formatBRL(p.valorAvaliacao)}</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--gn)', letterSpacing: '-.3px' }}>{formatBRL(p.valorMinimo)}</div>
          <div style={{ fontSize: 10, color: 'var(--br)', fontWeight: 700 }}>−{p.desconto}%</div>
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); toggleFav(p.id); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0 }}
        >
          {isFav ? <HeartFilled s={15} /> : <Ic name="heart" s={15} c="var(--t3)" />}
        </button>
      </div>
    );
  }

  /* ── Grid card ── */
  return (
    <div
      className="property-card card-shimmer"
      onClick={handleClick}
      style={{
        background: 'var(--sf)', border: '1px solid var(--bd)',
        borderRadius: 'var(--radius)', overflow: 'hidden', cursor: 'pointer',
        transition: 'transform .18s, box-shadow .18s, border-color .18s',
        boxShadow: 'var(--shadow-sm)',
        ...inactiveStyle,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
        e.currentTarget.style.borderColor = 'rgba(184,134,42,.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
        e.currentTarget.style.borderColor = 'var(--bd)';
      }}
    >
      {/* Thumbnail */}
      <div style={{
        height: 116, position: 'relative',
        background: TYPE_GRADIENT[p.tipo] || 'var(--ct)',
        backgroundImage: p.images?.[0] ? `url(${p.images[0]})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!p.images?.[0] && <Logo size={32} />}

        {/* Top-left: status + AI badge */}
        <div style={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
          {enriched && (
            <span style={{
              background: 'rgba(37,99,235,.85)', color: '#fff',
              padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
              backdropFilter: 'blur(4px)', letterSpacing: '.5px',
              display: 'inline-flex', alignItems: 'center', gap: 3,
            }}>
              <Ic name="ai" s={8} c="#fff" /> IA
            </span>
          )}
        </div>

        {/* Top-right: fav button */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleFav(p.id); }}
          style={{
            position: 'absolute', top: 8, right: 8,
            background: isFav ? 'rgba(239,68,68,.15)' : 'rgba(0,0,0,.35)',
            backdropFilter: 'blur(6px)',
            border: isFav ? '1px solid rgba(239,68,68,.3)' : '1px solid rgba(255,255,255,.15)',
            borderRadius: '50%', width: 28, height: 28, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background .15s',
          }}
        >
          {isFav ? <HeartFilled s={12} /> : <Ic name="heart" s={12} c="rgba(255,255,255,.8)" />}
        </button>

        {/* Urgency ribbon — red for auctions (<= 7d), blue for Venda Online */}
        {ribbonLabel && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: ribbonBg,
            backdropFilter: 'blur(4px)',
            color: '#fff', padding: '3px 10px', fontSize: 10, fontWeight: 700,
            textAlign: 'center', letterSpacing: '.5px',
          }}>
            {ribbonLabel}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '12px 13px 13px' }}>
        {/* Title */}
        <h3 style={{
          fontSize: 13, fontWeight: 600, marginBottom: 3, lineHeight: 1.35,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          color: 'var(--tx)', letterSpacing: '-.1px',
        }}>
          {p.titulo}
        </h3>

        {/* City / UF — Caixa encodes this in the title already ("Casa — CIDADE/UF")
            but Santander titles are the street name only, so the city would
            otherwise not appear on Santander cards. */}
        {p.cidade && p.estado && (
          <div style={{
            fontSize: 11, color: 'var(--t2)', marginBottom: 6, fontWeight: 500,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {p.cidade}/{p.estado}
          </div>
        )}

        {/* Specs row */}
        <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 10, display: 'flex', gap: 8 }}>
          {p.area  && <span>{p.area}m²</span>}
          {p.quartos > 0 && <span>{p.quartos} qts</span>}
          {p.vagas  > 0 && <span>{p.vagas} vg</span>}
        </div>

        {/* Divider */}
        <hr className="gold-divider" style={{ marginBottom: 10, opacity: .2 }} />

        {/* Price block */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 9 }}>
          <div>
            <div style={{ fontSize: 10, color: 'var(--t3)', textDecoration: 'line-through', marginBottom: 1 }}>
              {formatBRL(p.valorAvaliacao)}
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--gn)', letterSpacing: '-.4px', lineHeight: 1 }}>
              {formatBRL(p.valorMinimo)}
            </div>
          </div>
          <Badge variant="brand">−{p.desconto}%</Badge>
        </div>

        {/* Modalidade + payment methods */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8, alignItems: 'center' }}>
          <span style={{
            background: modBg, color: modFg,
            padding: '2px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700, letterSpacing: '.3px', textTransform: 'uppercase',
          }}>
            {p.modalidade}
          </span>
          {payments.map((pm) => (
            <span
              key={pm}
              title={PAYMENT_LABEL[pm]}
              style={{ background: 'var(--s2)', color: 'var(--t2)', padding: '2px 6px', borderRadius: 3, fontSize: 9, fontWeight: 600, border: '1px solid var(--bd)' }}
            >
              {PAYMENT_SHORT[pm]}
            </span>
          ))}
        </div>

        {/* Footer row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 10 }}>
          <span style={{
            background: bankStyle.bg, color: bankStyle.text,
            padding: '2px 7px', borderRadius: 4, fontWeight: 700, letterSpacing: '.3px',
          }}>
            {p.banco}
          </span>
          <span style={{ color: 'var(--t3)' }}>{formatDate(p.dataLeilao)}</span>
        </div>

        {/* AI insight */}
        {enrichment?.advantages?.[0] && (
          <div style={{
            marginTop: 9, padding: '6px 8px',
            background: 'var(--al)', border: '1px solid rgba(37,99,235,.12)',
            borderRadius: 'var(--radius-xs)', fontSize: 10, color: 'var(--ac)', lineHeight: 1.45,
          }}>
            <span style={{ fontWeight: 700 }}>IA: </span>{enrichment.advantages[0]}
          </div>
        )}
      </div>
    </div>
  );
}
